import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { products } from '../data/products';
import { DropdownProductCard } from './DropdownProductCard';

export function Navigation({ onCartOpen, cartItemsCount = 0, lightMode = false }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const logoSrc = lightMode && !isScrolled ? '/logo/berleto-logo.png' : '/logo/berleto-logo-dark.png';

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 w-full z-50 overflow-visible transition-all duration-300"
      style={{
        background: isScrolled ? 'rgba(250,249,246,0.95)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(12px)' : 'none',
        borderBottom: isScrolled ? '1px solid rgba(26,42,26,0.07)' : '1px solid transparent',
      }}
    >
      <div className="container mx-auto px-6">
        <div className="relative flex items-center justify-between h-20">

          {/* Logo + wordmark */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="flex items-center gap-3"
          >
            <img
              src={logoSrc}
              alt="Berleto Logo"
              className="h-11 w-11 object-contain transition-transform duration-700 hover:rotate-180"
            />
            <span
              className="text-base font-medium tracking-[0.22em] uppercase"
              style={{ color: lightMode && !isScrolled ? '#FAF9F6' : '#1A2A1A' }}
            >
              Berleto
            </span>
          </a>

          {/* Desktop nav — absolutely centered */}
          <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">

            {/* Shop with dropdown */}
            <div className="relative">
              <button
                className="flex items-center gap-1 text-sm tracking-wide transition-colors"
                style={{ color: lightMode && !isScrolled ? 'rgba(250,249,246,0.75)' : 'rgba(26,42,26,0.55)' }}
                onMouseEnter={() => setIsShopDropdownOpen(true)}
                onMouseLeave={() => setIsShopDropdownOpen(false)}
                onClick={() => navigate('/products')}
                onFocus={() => setIsShopDropdownOpen(true)}
              >
                Shop
                <ChevronDown
                  className="w-3.5 h-3.5 transition-transform duration-200"
                  style={{ transform: isShopDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>

              <AnimatePresence>
                {isShopDropdownOpen && (
                  <div
                    className="absolute top-full pt-5 left-1/2 -translate-x-1/2"
                    onMouseEnter={() => setIsShopDropdownOpen(true)}
                    onMouseLeave={() => setIsShopDropdownOpen(false)}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                      style={{
                        background: '#FAF9F6',
                        border: '1px solid rgba(26,42,26,0.08)',
                        width: '820px',
                        boxShadow: '0 16px 48px rgba(26,42,26,0.1)',
                      }}
                    >
                      <div className="grid grid-cols-3 divide-x" style={{ borderColor: 'rgba(26,42,26,0.06)' }}>
                        {Object.values(products).map((product, index) => (
                          <DropdownProductCard
                            key={product.id}
                            product={product}
                            index={index}
                            onClose={() => setIsShopDropdownOpen(false)}
                          />
                        ))}
                      </div>
                      <div
                        className="px-8 py-3 flex justify-between items-center border-t"
                        style={{ borderColor: 'rgba(26,42,26,0.06)', background: '#F0EDE6' }}
                      >
                        <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.4)' }}>
                          Free shipping on all orders
                        </span>
                        <button
                          onClick={() => { navigate('/bundle'); setIsShopDropdownOpen(false); }}
                          className="text-xs tracking-wide transition-colors"
                          style={{ color: '#B8704D' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#a56340'}
                          onMouseLeave={e => e.currentTarget.style.color = '#B8704D'}
                        >
                          Bundle & Save →
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {[
              { label: 'Science', path: '/science' },
              { label: 'About', path: '/about' },
            ].map(({ label, path }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className="text-sm tracking-wide transition-colors"
                style={{ color: lightMode && !isScrolled ? 'rgba(250,249,246,0.75)' : 'rgba(26,42,26,0.55)' }}
                onMouseEnter={e => e.currentTarget.style.color = lightMode && !isScrolled ? '#FAF9F6' : '#1A2A1A'}
                onMouseLeave={e => e.currentTarget.style.color = lightMode && !isScrolled ? 'rgba(250,249,246,0.75)' : 'rgba(26,42,26,0.55)'}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Right: cart + mobile burger */}
          <div className="flex items-center gap-3">
            <button
              onClick={onCartOpen}
              className="relative flex items-center justify-center w-9 h-9 transition-colors"
              style={{ color: lightMode && !isScrolled ? 'rgba(250,249,246,0.8)' : 'rgba(26,42,26,0.6)' }}
              onMouseEnter={e => e.currentTarget.style.color = lightMode && !isScrolled ? '#FAF9F6' : '#1A2A1A'}
              onMouseLeave={e => e.currentTarget.style.color = lightMode && !isScrolled ? 'rgba(250,249,246,0.8)' : 'rgba(26,42,26,0.6)'}
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemsCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 text-white text-[10px] w-4 h-4 flex items-center justify-center"
                  style={{ background: '#B8704D', borderRadius: '50%' }}
                >
                  {cartItemsCount}
                </span>
              )}
            </button>

            {/* Mobile burger */}
            <button
              className="md:hidden relative w-9 h-9 flex items-center justify-center"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="sr-only">Menu</span>
              <div className="relative w-5 h-3.5">
                <span className={`absolute h-px w-5 transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : 'translate-y-0'}`} style={{ background: '#1A2A1A' }} />
                <span className={`absolute h-px w-5 transition-all duration-300 translate-y-1.5 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} style={{ background: '#1A2A1A' }} />
                <span className={`absolute h-px w-5 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 translate-y-1.5' : 'translate-y-3'}`} style={{ background: '#1A2A1A' }} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t"
            style={{ background: '#FAF9F6', borderColor: 'rgba(26,42,26,0.07)' }}
          >
            <div className="container mx-auto px-6 py-6 space-y-1">
              <div className="pb-4 border-b mb-4" style={{ borderColor: 'rgba(26,42,26,0.07)' }}>
                <button
                  onClick={() => { navigate('/products'); setIsMobileMenuOpen(false); }}
                  className="text-sm font-medium tracking-wide mb-3 block"
                  style={{ color: '#1A2A1A' }}
                >
                  Shop
                </button>
                {Object.values(products).map((product) => (
                  <button
                    key={product.id}
                    onClick={() => { navigate(`/products/${product.id}`); setIsMobileMenuOpen(false); }}
                    className="block text-sm py-2 pl-4 w-full text-left transition-colors"
                    style={{ color: 'rgba(26,42,26,0.5)' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#B8704D'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(26,42,26,0.5)'}
                  >
                    {product.title}
                  </button>
                ))}
              </div>
              {[
                { label: 'Science', path: '/science' },
                { label: 'About', path: '/about' },
              ].map(({ label, path }) => (
                <button
                  key={path}
                  onClick={() => { navigate(path); setIsMobileMenuOpen(false); }}
                  className="block text-sm tracking-wide py-2 w-full text-left"
                  style={{ color: 'rgba(26,42,26,0.55)' }}
                >
                  {label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
